/* eslint-disable max-lines-per-function */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable sonarjs/cognitive-complexity */
import {
  ChangeEvent,
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

import { useEventEmitter } from "@/v1/hooks/useEventEmitter";

import {
  FormContext as IFormContext,
  FormErrorsKeys,
  FormErrorsType,
  FormStateType,
  RegisterProps,
  SelectOption,
} from "../types";
import { checkFormError } from "../utils/checkFormError";
import { FormEvents } from "../utils/constants";
import { errorChangeEvent } from "../utils/errorChangeEvent";
import { hasValueChanged } from "../utils/hasValueChanged";
import { valueChangeEvent } from "../utils/valueChangeEvent";
import { valueSetEvent } from "../utils/valueSetEvent";

const FormContext = createContext({} as IFormContext);

interface FormProviderProps {
  children: ReactNode;
  checkErrorsOnChange?: boolean;
}

type FormStateRef = { [error: string]: string | undefined | number | SelectOption };
type ErrorRefType = { [inputName: string]: FormErrorsType | undefined };
type ErrorMessagesRefType = { [error: string]: string | undefined };

export const FormProvider: FC<FormProviderProps> = ({ children, checkErrorsOnChange }) => {
  const formStateRef = useRef<FormStateRef>({});
  const errorsRef = useRef<ErrorRefType>({});
  const errorMessagesRef = useRef<ErrorMessagesRefType>({});
  const { subscribe, dispatch } = useEventEmitter();

  const setError = useCallback((name: string, errorMessage?: string) => {
    errorMessagesRef.current = {
      ...errorMessagesRef.current,
      [name]: errorMessage,
    };
    dispatch(errorChangeEvent(name), errorMessage);
  }, []);

  const getError = useCallback((name: string) => errorMessagesRef.current[name], []);

  const handleInputChange = useCallback(
    (name: string, checkErrors?: boolean) => (event: string | ChangeEvent<HTMLInputElement>) => {
      let inputValue: string | boolean | ChangeEvent<HTMLInputElement> = event;

      const isReactEvent = event && typeof event !== "string" && event.type === "change";
      if (isReactEvent) {
        if (event.persist) event.persist();
        inputValue = event.target?.type === "checkbox" ? event.target.checked : event.target?.value;
      }

      if (!hasValueChanged(getState(), name, inputValue as string)) return;

      const newState = {
        ...formStateRef.current,
        [name]: inputValue as string,
      };
      formStateRef.current = newState;
      dispatch(valueChangeEvent(name), inputValue);
      dispatch(FormEvents.STATE_CHANGE, newState);

      if (checkErrors) {
        const errorMessage = getChildErrors(name);

        if (errorMessage || (errorMessagesRef.current[name] && !errorMessage)) {
          setError(name, errorMessage as string);
        }
      }
    },
    [],
  );

  const getChildErrors = useCallback((inputName: string) => {
    const errors = errorsRef.current[inputName];
    const inputValue = formStateRef.current[inputName] as string;
    let errorMessage: string | boolean = false;

    for (const errorName in errors) {
      const errorData = errors[errorName];
      const isErrorDataArray = Array.isArray(errorData);
      const checkErrorFn = checkFormError[errorName as FormErrorsKeys];
      let hasError = false;

      if (checkErrorFn) {
        hasError = isErrorDataArray
          ? checkErrorFn(inputValue, errorData[0])
          : checkErrorFn(inputValue);
      } else if (isErrorDataArray && typeof errorData[0] === "function") {
        hasError = errorData[0](inputValue, formStateRef.current);
      }

      if (hasError) {
        errorMessage = isErrorDataArray ? errorData[1] : errorData;
        break;
      }
    }

    return errorMessage;
  }, []);

  const setValue = useCallback((name: string, value?: any) => {
    if (!hasValueChanged(getState(), name, value)) return;

    formStateRef.current = {
      ...formStateRef.current,
      [name]: value,
    };

    dispatch(valueSetEvent(name), value);
  }, []);

  const checkChildErrors = useCallback(
    () =>
      new Promise<boolean>((res) => {
        let hasErrors = false;

        for (const name in formStateRef.current) {
          const errorMessage = getChildErrors(name);

          if (errorMessage || (errorMessagesRef.current[name] && !errorMessage)) {
            setError(name, errorMessage as string);
          }

          if (errorMessage) hasErrors = true;
        }

        res(hasErrors);
      }),
    [],
  );

  const unregister = useCallback((name: string) => {
    const { [name]: _value, ...restState } = formStateRef.current;
    const { [name]: _errors, ...restErrors } = errorsRef.current;
    const { [name]: _message, ...restMessages } = errorMessagesRef.current;

    formStateRef.current = restState;
    errorsRef.current = restErrors;
    errorMessagesRef.current = restMessages;
  }, []);

  const getState = useCallback(() => formStateRef.current, []);
  const getValue = useCallback((name: string) => getState()[name], []);

  const resetForm = useCallback(() => {
    const newFormState: FormStateType = {};

    for (const field in formStateRef.current) {
      if (formStateRef.current[field]) {
        dispatch(valueChangeEvent(field));
      }
      newFormState[field] = undefined;
    }

    formStateRef.current = newFormState;
    dispatch(FormEvents.RESET_FORM, newFormState);
  }, []);

  const register = useCallback(
    ({ name, errors, defaultValue, defaultChecked, type, value }: RegisterProps) => {
      let currentValue = defaultValue || defaultChecked;

      if (type === "radio") currentValue = defaultChecked ? value : formStateRef.current[name];

      formStateRef.current = {
        ...formStateRef.current,
        [name]: currentValue,
      };

      errorsRef.current = {
        ...errorsRef.current,
        [name]: errors,
      };

      return {
        onChange: handleInputChange(name, checkErrorsOnChange),
      };
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      resetForm,
      getState,
      getValue,
      checkChildErrors,
      subscribe,
      register,
      setValue,
      unregister,
      setError,
      getError,
    }),
    [
      resetForm,
      getState,
      checkChildErrors,
      subscribe,
      register,
      setValue,
      unregister,
      setError,
      getError,
    ],
  );

  return <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>;
};

export const useFormContext = () => {
  return useContext(FormContext);
};
